/**
 * Extra speed a solid hit takes off, for a human and a bot alike. The bounce
 * still aims the hull. This only spends leftover speed. Turn it off and a hit
 * keeps the shore curve alone (`shoreKeepFraction`).
 *
 * Edit `CRASH_COST` to retune. No other file holds these numbers.
 */

import { CRASH_SPEED_REF } from './constants.ts';
import type { BoatState } from './types.ts';

export type CrashCostSettings = {
  enabled: boolean;
  /**
   * Movement-radii of speed removed for a real hit. One radius is
   * `CRASH_SPEED_REF` (30 m), about one full turn of acceleration.
   */
  contactTurns: number;
  /** Also remove this multiple of whatever speed the bounce would have kept. */
  keptSpeedTurns: number;
};

/**
 * 4 turns is 120 m of speed, and the kept-speed term takes the rest.
 * A hard hit stops the boat. From a dead heat the boat that stayed in the water
 * pulls away while this one starts again.
 */
export const CRASH_COST: CrashCostSettings = {
  enabled: true,
  contactTurns: 4,
  keptSpeedTurns: 1,
};

/** Circle size at drive 0, 1, and 2. The third turn after a stop is the full hull. */
export const DRIVE_SCALE = [0.4, 0.7, 1] as const;

export type DriveLevel = 0 | 1 | 2;

/** A brush under this shore angle drops one pace step instead of stopping the boat. */
export const SCRAPE_CROSSING_DEG = 20;

/** Share of the shore-curve speed a scrape keeps. */
export const SCRAPE_SPEED_KEEP = 0.7;

/** Speed left after the crash cost. The incoming value is the shore-curve leftover. */
export function applyCrashCost(
  outgoing: number,
  settings: CrashCostSettings = CRASH_COST,
): number {
  if (!settings.enabled) {
    return Math.max(0, outgoing);
  }

  const shed =
    settings.contactTurns * CRASH_SPEED_REF +
    settings.keptSpeedTurns * Math.max(0, outgoing);

  return Math.max(0, outgoing - shed);
}

export function driveLevel(boat: BoatState): DriveLevel {
  if (boat.drive === 0 || boat.drive === 1 || boat.drive === 2) {
    return boat.drive;
  }

  return boat.speed > 0 ? 2 : 0;
}

export function nextDrive(boat: BoatState): DriveLevel {
  return Math.min(2, driveLevel(boat) + 1) as DriveLevel;
}

export function scrapeDrive(boat: BoatState): DriveLevel {
  return Math.max(0, driveLevel(boat) - 1) as DriveLevel;
}

export function isScrape(crossingDeg: number | null): boolean {
  return crossingDeg !== null && crossingDeg < SCRAPE_CROSSING_DEG;
}
